import { DeploymentEnvironments } from '@missionlabs/types'
import { getKeycloakEnvironment } from '@missionlabs/utils'
import { Buffer } from 'buffer'
import isElectron from 'is-electron'
import Keycloak from 'keycloak-js'

import {
    ELECTRON_KEYCLOAK_REDIRECT_URI,
    isKeycloakCallbackUrl,
    takeStoredKeycloakCallback
} from './keycloakCallback'
import { getKeycloakInitOptions, rememberPostLoginRedirect } from './keycloakSession'

const keycloakConfig = {
    url: '',
    realm: '',
    clientId: ''
}

export let keycloak = new Keycloak(keycloakConfig)

let initPromise: Promise<boolean> | undefined
let appliedConfigKey = ''

function configIdentity(config: { url: string; realm: string; clientId: string }) {
    return `${config.url}\0${config.realm}\0${config.clientId}`
}

function syncKeycloakFields(instance: Keycloak) {
    instance.authServerUrl = keycloakConfig.url
    instance.realm = keycloakConfig.realm
    instance.clientId = keycloakConfig.clientId
}

function applyKeycloakConfig(environment?: DeploymentEnvironments) {
    const next = getKeycloakEnvironment(environment)
    const nextKey = configIdentity(next)
    if (nextKey === appliedConfigKey) return

    keycloakConfig.url = next.url
    keycloakConfig.realm = next.realm
    keycloakConfig.clientId = next.clientId

    if (!keycloak.didInitialize) {
        keycloak = new Keycloak({
            url: next.url,
            realm: next.realm,
            clientId: next.clientId
        })
        initPromise = undefined
    } else {
        syncKeycloakFields(keycloak)
    }

    appliedConfigKey = nextKey
}

export function canUseKeycloak() {
    if (!keycloakConfig.url || !keycloakConfig.realm || !keycloakConfig.clientId) return false
    if (typeof window === 'undefined') return false
    // Packaged Electron loads from file://. The login itself opens in a child window.
    if (isElectron()) return true
    return window.location.protocol === 'http:' || window.location.protocol === 'https:'
}

export function initKeycloak(environment?: DeploymentEnvironments): Promise<boolean> {
    applyKeycloakConfig(environment)
    if (!canUseKeycloak()) return Promise.resolve(false)
    if (!initPromise) {
        const instance = keycloak
        if (instance.didInitialize) {
            syncKeycloakFields(instance)
            initPromise = Promise.resolve(Boolean(instance.authenticated))
        } else {
            instance.onTokenExpired = () => {
                instance.updateToken(30).catch(error => {
                    console.error('Failed to refresh Keycloak token', error)
                })
            }
            initPromise = instance
                .init(getKeycloakInitOptions())
                .then(authenticated => {
                    // init copies the constructor config. Re-apply in case the
                    // login environment changed while initialization was in flight.
                    syncKeycloakFields(instance)
                    return authenticated
                })
                .catch(error => {
                    console.error('Failed to initialize Keycloak', error)
                    return false
                })
        }
    }
    return initPromise
}

export function readKeycloakUsername() {
    const parsed = keycloak.idTokenParsed ?? keycloak.tokenParsed
    if (!parsed) return
    if (typeof parsed.preferred_username === 'string') return parsed.preferred_username
    if (typeof parsed.email === 'string') return parsed.email
}

function parseJwtPayload(token: string): { nonce?: string } {
    const payload = token.split('.')[1]
    if (!payload) throw new Error('Keycloak token was invalid')
    const json = Buffer.from(payload.replace(/-/g, '+').replace(/_/g, '/'), 'base64').toString(
        'utf8'
    )
    return JSON.parse(json)
}

async function exchangeAuthorizationCode(callbackUrl: string) {
    if (!isKeycloakCallbackUrl(callbackUrl, ELECTRON_KEYCLOAK_REDIRECT_URI)) {
        throw new Error('Keycloak sign-in returned an unexpected redirect')
    }

    const url = new URL(callbackUrl)
    const error = url.searchParams.get('error')
    if (error) throw new Error(url.searchParams.get('error_description') || error)

    const code = url.searchParams.get('code')
    const state = url.searchParams.get('state')
    if (!code || !state) throw new Error('Keycloak sign-in did not return a code')

    const stored = takeStoredKeycloakCallback(state)
    if (!stored?.pkceCodeVerifier || !stored.redirectUri) {
        throw new Error('Keycloak sign-in state was not found')
    }

    const tokenUrl = `${keycloakConfig.url.replace(/\/$/, '')}/realms/${
        keycloakConfig.realm
    }/protocol/openid-connect/token`
    const response = await fetch(tokenUrl, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new URLSearchParams({
            code,
            grant_type: 'authorization_code',
            client_id: keycloakConfig.clientId,
            redirect_uri: stored.redirectUri,
            code_verifier: stored.pkceCodeVerifier
        })
    })
    if (!response.ok) throw new Error('Keycloak token exchange failed')

    const tokens: { access_token?: string; refresh_token?: string; id_token?: string } =
        await response.json()
    if (!tokens.id_token || !tokens.access_token) {
        throw new Error('Keycloak did not return an ID token')
    }

    const idTokenParsed = parseJwtPayload(tokens.id_token)
    if (stored.nonce && idTokenParsed.nonce !== stored.nonce) {
        throw new Error('Keycloak sign-in nonce did not match')
    }

    keycloak.authenticated = true
    keycloak.token = tokens.access_token
    keycloak.refreshToken = tokens.refresh_token
    keycloak.idToken = tokens.id_token
    keycloak.tokenParsed = parseJwtPayload(tokens.access_token)
    keycloak.idTokenParsed = idTokenParsed
    return tokens.id_token
}

export async function startKeycloakLogin(
    username: string,
    redirectPath: string,
    environment?: DeploymentEnvironments
): Promise<string | void> {
    await initKeycloak(environment)
    if (!keycloak.didInitialize) {
        throw new Error('Keycloak is not configured')
    }
    rememberPostLoginRedirect(redirectPath)

    const redirectUri = isElectron()
        ? ELECTRON_KEYCLOAK_REDIRECT_URI
        : `${window.location.origin}/login`

    if (isElectron()) {
        const authUrl = await keycloak.createLoginUrl({
            loginHint: username,
            redirectUri,
            prompt: 'login'
        })
        const callbackUrl = await window.electron.keycloakLogin(authUrl, redirectUri)
        return exchangeAuthorizationCode(callbackUrl)
    }

    await keycloak.login({
        loginHint: username,
        redirectUri,
        // Reuse of the Keycloak cookie produces an id token with acr "0".
        // /login/keycloak rejects that. Force a fresh sign-in.
        prompt: 'login'
    })
}
